import { useState, ChangeEvent, useEffect } from "react";
import {
  Box,
  Card,
  CardContent,
  Button,
  Stepper,
  Step,
  StepLabel,
  Snackbar,
  Alert,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import toast from "react-hot-toast";
import TermsAndConditions from "./TermsAndConditions";
import { useRouter } from "src/hooks/use-router";
import config from "../../../apiServices/appConfig";
import { FormData, DocumentUpload } from "./types";
import { createDocumentUpload } from "./utils";
import documentKeyToEnum from "./documentKeyToEnum";
import BusinessInfoStep from "./BusinessInfoStep";
import LegalDocumentsStep from "./LegalDocumentsStep";
import ReviewStep from "./ReviewStep";
import DocumentUploadField from "./DocumentUploadField";
import { kybService } from "../../../services/kyb.service";
import axios from "axios";
import { useDispatch } from "react-redux";
import { setUserKyb } from "src/slices/userKybSlice";
const { BASE_URL, API_VERSION } = config;



const steps = ["Business Information", "Legal Documents", "Review"];

const businessTypes = [
  "Sole Proprietorship",
  "Partnership",
  "Private Limited Company",
  "Public Limited Company",
  "LLC",
  "Other",
];

const initialFormData: FormData = {
  businessName: "",
  businessType: "",
  registrationNumber: "",
  taxId: "",
  incorporationDate: "",
  businessAddress: "",
  city: "",
  state: "",
  country: "",
  postalCode: "",
  phone: "",
  email: "",
  website: "",
  moaAoa: createDocumentUpload(),
  certificateOfIncorporation: createDocumentUpload(),
  pan_entity: createDocumentUpload(),
  gstRegistrationCertificate: createDocumentUpload(),
  listOfShareholders: createDocumentUpload(),
  listOfDirectors: createDocumentUpload(),
  kycDirectors: createDocumentUpload(),
  kycUbos: createDocumentUpload(),
  bankStatementOneYear: createDocumentUpload(),
  itrTwoYears: createDocumentUpload(),
  declarationNatureOfBusiness: createDocumentUpload(),
  declarationAuthorisedSignatories: createDocumentUpload(),
  declarationSourceOfWealth: createDocumentUpload(),
  partnershipDeed: createDocumentUpload(),
  msmeUdyamAayogCertificate: createDocumentUpload(),
  gstCertificate: createDocumentUpload(),
  registrationCertificate: createDocumentUpload(),
  itrTwoFinancialYears: createDocumentUpload(),
  panOfFirm: createDocumentUpload(),
  bankStatementOneYearPartnership: createDocumentUpload(),
  operationalOfficeAddressProof: createDocumentUpload(),
  kycAllPartners: createDocumentUpload(),
  declarationsAndUndertakings: createDocumentUpload(),
  owners: [
    {
      name: "",
      position: "",
      nationality: "",
      idType: "",
      idNumber: "",
      ownershipPercentage: "",
      address: "",
    },
  ],
};

const KYB = () => {

 const dispatch = useDispatch()


  const currentUser = JSON.parse(localStorage.getItem("current_user") || "{}");
  const { client_id } = currentUser;
  


  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success" as "success" | "error" | "info",
  });
  const [uploadedDocs, setUploadedDocs] = useState<{ [key: string]: any }>({});
  const [loadingDocs, setLoadingDocs] = useState(true);
  const [pendingUpload, setPendingUpload] = useState<{
    key: keyof FormData;
    file: File;
  } | null>(null);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [hyperVergeStatus, setHyperVergeStatus] = useState<string | null>(null);
  const router = useRouter();

  // Responsive hooks
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const  [isAutoApproved , setIsApproved]= useState(false);

  const fetchUploadedDocs = async () => {
    setLoadingDocs(true);
    try {
      const access_token = localStorage.getItem("access_token");
      if (!access_token) {
        setUploadedDocs({});
        return;
      }
      const response = await fetch(`${BASE_URL}${API_VERSION}/kyb`, {
        headers: {
          Authorization: `Bearer ${access_token}`,
        },
      });
      if (!response.ok) {
        setUploadedDocs({});
        return;
      }
      const data = await response.json();
      const kybData = data?.data?.data?.kybData || []
      console.log(kybData,'from the KYB section')
      const backendToFrontends: Record<string, string[]> = {};
      Object.entries(documentKeyToEnum).forEach(([frontend, backend]) => {
        if (!backendToFrontends[backend]) backendToFrontends[backend] = [];
        backendToFrontends[backend].push(frontend);
      });
      const uploaded: { [key: string]: any } = {};
      kybData.forEach((doc: any) => {
        const frontendKeys = backendToFrontends[doc.document_type];
        if (frontendKeys) {
          frontendKeys.forEach((key) => {
            uploaded[key] = doc;
          });
        }
      });
      setUploadedDocs(uploaded);
    } catch (err) {
      setUploadedDocs({});
    } finally {
      setLoadingDocs(false);
    }
  };



  const getKybDataByUserId= async ()=>{
      try{

        const  token= localStorage.getItem("access_token");
        if(!token){
          return ;
        }

      const response = await axios.get(`${BASE_URL}${API_VERSION}/kyb/kyb-result/${client_id}`,{
        headers:{
          Authorization: `Bearer ${token}`,
        }
      })
      dispatch(setUserKyb(response?.data))
     
      const kybStatus= response?.data?.data?.response?.data?.metadata?.applicationStatus
        if(kybStatus == "auto_approved"){
           setIsApproved(true)
           setAcceptedTerms(true);
        }


      }catch(err){
         console.log(err)
      }
  }


    useEffect(()=>{
      getKybDataByUserId();
    },[])

  useEffect(() => {
    fetchUploadedDocs();
  }, []);

  const handleSelectChange = (e: any) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleFileChange = (
    e: ChangeEvent<HTMLInputElement>,
    documentKey: keyof FormData
  ) => {
    const { files } = e.target;
    if (files && files[0]) {
      setPendingUpload({ key: documentKey, file: files[0] });
    }
  };

  const uploadDocumentToAPI = async (
    file: File,
    documentKey: string
  ): Promise<any> => {
    try {
      const documentType = documentKeyToEnum[documentKey] || documentKey;
      const response = await kybService.uploadDocument({
        file,
        documentType,
      });
      if (response.status) {
        toast.success(
          `${documentKey.toLowerCase()} Document uploaded successfully`
        );
      }
      return response;
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Document upload failed");
      throw error;
    }
  };

  const removeFile = (documentKey: keyof FormData) => {
    setFormData((prev) => ({
      ...prev,
      [documentKey]: createDocumentUpload(),
    }));
  };

  const confirmAndUploadFile = async () => {
    if (!pendingUpload) return;
    const { key, file } = pendingUpload;
    setFormData((prev) => ({
      ...prev,
      [key]: {
        ...(prev[key] as DocumentUpload),
        file,
        status: "uploading",
      },
    }));
    try {
      const response = await uploadDocumentToAPI(file, key);
      setFormData((prev) => ({
        ...prev,
        [key]: {
          ...(prev[key] as DocumentUpload),
          status: "success",
          responseData: response,
        },
      }));
      setPendingUpload(null);
      await fetchUploadedDocs();
    } catch (error) {
      setFormData((prev) => ({
        ...prev,
        [key]: {
          ...(prev[key] as DocumentUpload),
          status: "error",
          errorMessage:
            error instanceof Error ? error.message : "Upload failed",
        },
      }));
      setPendingUpload(null);
    }
  };

  const renderDocumentUploadField = (
    documentKey: keyof FormData,
    label: string,
    required: boolean = true
  ) => (
    <DocumentUploadField
      documentKey={documentKey}
      label={label}
      required={required}
      formData={formData}
      uploadedDocs={uploadedDocs}
      pendingUpload={pendingUpload}
      handleFileChange={handleFileChange}
      removeFile={removeFile}
      confirmAndUploadFile={confirmAndUploadFile}
      setPendingUpload={setPendingUpload}
    />
  );

  const renderStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <BusinessInfoStep
            formData={formData}
            hyperVergeStatus={hyperVergeStatus}
            setHyperVergeStatus={setHyperVergeStatus}
            renderStepContent={renderStepContent}
            isAutoApproved={isAutoApproved}
          />
        );
      case 1:
        return (
          <LegalDocumentsStep
            formData={formData}
            handleSelectChange={handleSelectChange}
            businessTypes={businessTypes}
            renderDocumentUploadField={renderDocumentUploadField}
          />
        );
      case 2:
        return <ReviewStep />;
      default:
        return null;
    }
  };

  const handleNext = () => {
    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleSubmit = () => {
    router.push("/dashboard/profile");
  };

  if (!acceptedTerms) {
    return <TermsAndConditions onAccept={() => setAcceptedTerms(true)} />;
  }

  return (
    <Box
      sx={{
        width: "100%",
        px: isMobile ? 1 : 6,
        py: isMobile ? 1 : 4,
        minHeight: "100vh",
        boxSizing: "border-box",
        background: theme.palette.background.default,
      }}
    >
      <Stepper
        activeStep={activeStep}
        sx={{ mb: isMobile ? 2 : 3 }}
        orientation={isMobile ? "vertical" : "horizontal"}
      >
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>
      <Card
        sx={{
          maxWidth:"90vw",
          mx: "auto",
          width: "100%",
          boxShadow: isMobile ? 1 : 3,
        }}
      >
        <CardContent sx={{ p: isMobile ? 1 : 4 }}>
          {renderStepContent(activeStep)}
          <Box
            sx={{
              display: "flex",
              flexDirection: isMobile ? "column" : "row",
              justifyContent: "flex-end",
              mt: 2,
            }}
          >
            <Button
              disabled={activeStep === 0}
              onClick={handleBack}
              sx={{ mr: isMobile ? 0 : 1, mb: isMobile ? 1 : 0 }}
              fullWidth={isMobile}
            >
              Back
            </Button>
            {activeStep < steps.length - 1 ? (
              <Button
                variant="contained"
                onClick={handleNext}
                fullWidth={isMobile}
              >
                Next
              </Button>
            ) : (
              <Button
                variant="contained"
                onClick={handleSubmit}
                fullWidth={isMobile}
              >
                Submit
              </Button>
            )}
          </Box>
        </CardContent>
      </Card>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default KYB;
